import type { ReactNode } from 'react';

import type { SceneValidationRow, SceneValidationView } from '../../lib/api.js';
import { Icon } from '../Icon.js';
import { EmptyState } from '../ui/EmptyState.js';
import { SearchField } from '../ui/SearchField.js';
import { SegmentedFilter, type FilterOption } from '../ui/SegmentedFilter.js';
import { StatusDot } from '../ui/StatusPill.js';
import { navigatorKeyDown } from '../ui/SplitWorkspace.js';
import type { SceneFilter } from './sceneAddress.js';
import { INT_EXT_SHORT, STATUS_LABEL, STATUS_STATE, sceneLabel, warningText } from './sceneLabels.js';
import { SceneMarkers } from './ScenePanel.js';

/** The filters, in the order a reviewer works through them: what needs them first. */
export function sceneFilterOptions(view: SceneValidationView | undefined): FilterOption<SceneFilter>[] {
  const s = view?.summary;

  return [
    { id: 'ALL', label: 'All', count: s?.active },
    { id: 'NEEDS_ATTENTION', label: 'Needs attention', count: s?.needsAttention, tone: s !== undefined && s.needsAttention > 0 ? 'attention' : undefined },
    { id: 'TO_REVIEW', label: 'Not reviewed', count: s?.toReview },
    { id: 'CONFIRMED', label: 'Confirmed', count: s?.confirmed },
    { id: 'EXCLUDED', label: 'Excluded', count: s?.excluded },
  ];
}

export interface BulkState {
  readonly picked: ReadonlySet<string>;
  readonly toConfirm: number;
  readonly needingAttention: number;
  readonly confirmed: number;
  readonly busy: boolean;
  readonly note: string | null;
}

/**
 * THE SCENE NAVIGATOR: every scene as one compact row, the list scrolling on
 * its own beside the scene being read.
 *
 * A row is number, INT/EXT, heading and a status dot, with one line under it
 * only when there is something to say — why it needs attention, that it was
 * edited, added, merged or not read by the AI. Ninety scenes are ninety rows
 * somebody can scan, not ninety panels to scroll past. ↑ and ↓ walk the list.
 */
export function SceneNavigator({
  view,
  shown,
  scenes,
  filter,
  q,
  selectedId,
  refusal,
  bulk,
  onFilter,
  onSearch,
  onSelect,
  onTogglePick,
  onPickShown,
  onBulkConfirm,
  onBulkUnconfirm,
  onBulkClear,
  onAddFirst,
  onClearFilters,
}: {
  view: SceneValidationView | undefined;
  shown: readonly SceneValidationRow[];
  /** Every active scene, for the merged-into names. */
  scenes: readonly SceneValidationRow[];
  filter: SceneFilter;
  q: string;
  selectedId: string | null;
  refusal: string | null;
  bulk: BulkState;
  onFilter: (filter: SceneFilter) => void;
  onSearch: (q: string) => void;
  onSelect: (sceneId: string) => void;
  onTogglePick: (sceneId: string) => void;
  onPickShown: (pick: boolean) => void;
  onBulkConfirm: () => void;
  onBulkUnconfirm: () => void;
  onBulkClear: () => void;
  onAddFirst: () => void;
  onClearFilters: () => void;
}) {
  const ids = shown.map((s) => s.id);
  const pickable = filter !== 'EXCLUDED';
  const allPicked = shown.length > 0 && shown.every((s) => bulk.picked.has(s.id));
  const total = filter === 'EXCLUDED' ? (view?.excluded.length ?? 0) : scenes.length;

  return (
    <>
      <div className="ui-nav-head">
        <SearchField
          value={q}
          onChange={onSearch}
          placeholder="Search number, heading or text"
          label="Search scenes by number, heading or text"
          testId="scene-search"
        />
        <SegmentedFilter options={sceneFilterOptions(view)} value={filter} onChange={onFilter} label="Show scenes" testIdPrefix="scene-filter" />

        {pickable && shown.length > 0 ? (
          <div className="sw-pickbar">
            <label className="sw-pick-all">
              <input
                type="checkbox"
                aria-label="Select every scene shown"
                data-testid="scene-pick-all"
                checked={allPicked}
                onChange={(e) => {
                  onPickShown(e.target.checked);
                }}
              />
              <span>{bulk.picked.size === 0 ? 'Select' : `${String(bulk.picked.size)} selected`}</span>
            </label>
            {bulk.picked.size === 0 ? null : (
              <span className="sw-bulk" data-testid="scene-bulk" role="region" aria-label="Selected scenes">
                <span className="sr-only" data-testid="scene-bulk-count">{`${String(bulk.picked.size)} selected`}</span>
                <button
                  type="button"
                  className="ctl"
                  data-testid="scene-bulk-confirm"
                  disabled={bulk.toConfirm === 0 || bulk.busy || refusal !== null}
                  title={refusal ?? undefined}
                  onClick={onBulkConfirm}
                >
                  <Icon name="check" size={13} strokeWidth={2.6} />
                  {`Confirm ${String(bulk.toConfirm)}`}
                </button>
                <button
                  type="button"
                  className="ctl"
                  data-testid="scene-bulk-unconfirm"
                  disabled={bulk.confirmed === 0 || bulk.busy || refusal !== null}
                  title={refusal ?? undefined}
                  onClick={onBulkUnconfirm}
                >
                  Mark not reviewed
                </button>
                <button type="button" className="ctl" data-testid="scene-bulk-clear" onClick={onBulkClear}>
                  Clear
                </button>
              </span>
            )}
          </div>
        ) : null}
        {bulk.needingAttention > 0 ? (
          <p className="sw-bulk-note" data-testid="scene-bulk-attention">
            {`${String(bulk.needingAttention)} of these ${bulk.needingAttention === 1 ? 'needs' : 'need'} attention — open ${bulk.needingAttention === 1 ? 'it' : 'each one'} to confirm. Bulk confirm leaves ${bulk.needingAttention === 1 ? 'it' : 'them'} out.`}
          </p>
        ) : null}
        {bulk.note === null || bulk.picked.size > 0 ? null : (
          <p className="sw-bulk-done" role="status" data-testid="scene-bulk-result">
            {bulk.note}
          </p>
        )}
      </div>

      {shown.length === 0 ? (
        <div className="sw-nav-empty">
          <EmptyState
            testId="scene-table-empty"
            title={
              scenes.length === 0 && filter !== 'EXCLUDED'
                ? 'No scenes yet'
                : filter === 'EXCLUDED' && q.trim() === ''
                  ? 'Nothing has been excluded'
                  : 'No scenes match'
            }
            action={
              q.trim() !== '' || filter !== 'ALL' ? (
                <button type="button" className="ctl" data-testid="scene-filter-clear" onClick={onClearFilters}>
                  Clear search &amp; filters
                </button>
              ) : undefined
            }
          >
            {scenes.length === 0 && filter !== 'EXCLUDED' ? 'There are no scenes in this screenplay yet. Add the first one.' : undefined}
          </EmptyState>
        </div>
      ) : (
        <ul
          className="ui-nav-list sw-list"
          data-testid="scene-table"
          aria-label="Scenes"
          onKeyDown={navigatorKeyDown(ids, selectedId, onSelect, (id) => `[data-nav-id="${id}"]`)}
        >
          {shown.map((scene) => (
            <SceneRow
              key={scene.id}
              scene={scene}
              mergedInto={scene.mergedIntoSceneId === null ? null : (scenes.find((s) => s.id === scene.mergedIntoSceneId) ?? null)}
              selected={scene.id === selectedId}
              pickable={pickable}
              picked={bulk.picked.has(scene.id)}
              onSelect={() => {
                onSelect(scene.id);
              }}
              onTogglePick={() => {
                onTogglePick(scene.id);
              }}
            />
          ))}
        </ul>
      )}

      <div className="ui-nav-foot sw-nav-foot">
        <span data-testid="scene-table-count">{`${String(shown.length)} of ${String(total)} shown`}</span>
        <button
          type="button"
          className="ctl"
          data-testid="scene-add-first"
          disabled={refusal !== null}
          title={refusal ?? undefined}
          onClick={onAddFirst}
        >
          + Scene before the first
        </button>
      </div>
    </>
  );
}

function SceneRow({
  scene,
  mergedInto,
  selected,
  pickable,
  picked,
  onSelect,
  onTogglePick,
}: {
  scene: SceneValidationRow;
  mergedInto: SceneValidationRow | null;
  selected: boolean;
  pickable: boolean;
  picked: boolean;
  onSelect: () => void;
  onTogglePick: () => void;
}) {
  const excluded = scene.status === 'EXCLUDED';
  const firstWarning = scene.warnings[0];
  const markers = <SceneMarkers scene={scene} />;

  return (
    <li className="sw-row" data-testid={`scene-row-${scene.id}`} data-status={scene.status} data-selected={selected ? '1' : '0'}>
      {pickable && !excluded ? (
        <input
          type="checkbox"
          className="sw-pick"
          data-testid={`scene-pick-${scene.id}`}
          aria-label={`Select scene ${sceneLabel(scene)}`}
          checked={picked}
          onChange={onTogglePick}
        />
      ) : (
        <span className="sw-pick" aria-hidden="true" />
      )}
      <button
        type="button"
        className="ui-row"
        data-testid={`scene-review-${scene.id}`}
        data-nav-id={scene.id}
        aria-current={selected ? 'true' : undefined}
        onClick={onSelect}
      >
        <span className="ui-row-num">{sceneLabel(scene)}</span>
        <span className="ui-row-main">
          <span className="ui-row-title">
            <span className="sw-ie">{scene.intExt === null ? '—' : (INT_EXT_SHORT[scene.intExt] ?? scene.intExt)}</span>
            <span className="sw-heading scene-mono">{scene.slugline ?? 'Untitled scene'}</span>
          </span>
          {excluded ? (
            <span className="ui-row-sub">
              {scene.exclusionReason === 'MERGED'
                ? `Merged into ${mergedInto === null ? 'another scene' : `scene ${sceneLabel(mergedInto)}`}`
                : 'Not a scene'}
            </span>
          ) : firstWarning !== undefined ? (
            <span className="ui-row-sub sw-row-why">
              <span data-testid={`scene-warning-summary-${scene.id}`}>
                {warningText(firstWarning, scene).title}
                {scene.warnings.length > 1 ? ` +${String(scene.warnings.length - 1)}` : ''}
              </span>
              {markers}
            </span>
          ) : (
            <MarkersLine scene={scene}>{markers}</MarkersLine>
          )}
        </span>
        <span className="ui-row-end">
          <StatusDot state={STATUS_STATE[scene.status]} label={STATUS_LABEL[scene.status]} testId={`scene-status-${scene.id}`} />
        </span>
      </button>
    </li>
  );
}

/** The markers line, drawn only when the scene carries one. */
function MarkersLine({ scene, children }: { scene: SceneValidationRow; children: ReactNode }) {
  const any =
    (scene.originalSlugline !== null && scene.originalSlugline !== scene.slugline) ||
    scene.origin === 'MANUAL' ||
    scene.analysis === 'OUT_OF_DATE' ||
    scene.needsReading;

  return any ? <span className="ui-row-sub">{children}</span> : null;
}
